import { useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Feature, Subscription } from '../../models';
import { fromAccounts, fromAuth, fromSubscription, useAppDispatch } from '../../store';
import { Page } from '../../components/page';
import ButtonToLicenseManagement from '../payment-status/ButtonToLicenseManagement';
import { TrueSubscription } from './component/true-platform/TrueSubscription';
import { TruePlatformWelcome } from './component/true-platform/TruePlatformWelcome';
import { useGetTrackingCookies } from '../../hook';
import { withSubscriptions } from '../../utils/analytics';
import { sendTrackingData, TrackingPayload } from '../../utils/utils';
import { isTrialSubscription } from './subscriptionTrial';

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 20,
    color: '#22283C',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(3),
    alignItems: 'stretch',
    width: '100%',
    minWidth: 0,
    [theme.breakpoints.down(1300)]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('tabletL')]: {
      gridTemplateColumns: '1fr',
    },
  },
}));

const groupSubscriptionsByFeature = (subscriptions: Subscription[]): Subscription[][] => {
  const groups = new Map<Feature, Subscription[]>();
  subscriptions.forEach(subscription => {
    const group = groups.get(subscription.feature) ?? [];
    group.push(subscription);
    groups.set(subscription.feature, group);
  });

  return Array.from(groups.values());
};

const getPrimarySubscription = (subscriptions: Subscription[]) => (
  subscriptions.find(subscription => !isTrialSubscription(subscription))
    ?? subscriptions[0]
);

const SubscriptionV2Content = (
  { trueSubscriptions, accountId }: { trueSubscriptions: Subscription[]; accountId: number },
) => {
  const classes = useStyles();
  const trackingCookies = useGetTrackingCookies();
  const trackedViewAccountRef = useRef<number>();
  const subscriptionGroups = useMemo(
    () => groupSubscriptionsByFeature(trueSubscriptions),
    [trueSubscriptions],
  );
  const trackSubscriptionAction = useCallback((payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'subscription_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: 'true_bundle',
        ...payload,
        ...trackingCookies,
      },
    );
  }, [accountId, trackingCookies]);

  useEffect(() => {
    if (!accountId
      || trackedViewAccountRef.current === accountId
      || trueSubscriptions.length === 0) {
      return;
    }

    trackedViewAccountRef.current = accountId;
    sendTrackingData(
      'view_subscription_management',
      accountId,
      undefined,
      withSubscriptions(
        {
          page: 'subscription_management',
          plan_flow: 'true_bundle',
          ...trackingCookies,
        },
        trueSubscriptions,
      ),
    );
  }, [accountId, trueSubscriptions, trackingCookies]);

  if (trueSubscriptions.length === 0) {
    return null;
  }

  return (
    <Box paddingY={2}>
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          <FormattedMessage id="page.subscription.current.subscription.title" />
        </Typography>
        <ButtonToLicenseManagement
          title="go.to.true.platform"
          width={300}
          height={50}
          fontWeight={700}
          marginRight={1}
          onClick={() => trackSubscriptionAction({
            field: 'subscription_header',
            object: 'go_to_true_platform',
            action: 'click',
          })}
        />
      </Box>

      <Box className={classes.contentGrid}>
        {subscriptionGroups.map(subscriptionGroup => {
          const primarySubscription = getPrimarySubscription(subscriptionGroup);
          return (
            <TrueSubscription
              key={primarySubscription.id}
              trueSubscription={primarySubscription}
              subscriptions={subscriptionGroup}
              trackSubscriptionAction={trackSubscriptionAction}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export const SubscriptionV2 = (
  { subscriptions, accountId }: { subscriptions: Subscription[]; accountId: number },
) => {
  const dispatch = useAppDispatch();
  const user = useSelector(fromAuth.selectUser);
  const upgradeToTrue = useSelector(fromSubscription.selectUpgradeToTrue);
  const trackingCookies = useGetTrackingCookies();

  const fetchResource = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromAccounts.doGetSalesforceLinkedAccount()),
      ]);
    }
  };

  return (
    <Page
      id="subscription"
      headerId="page.subscription.header"
      headerValue={{ userName: user?.firstName }}
      fetchData={fetchResource}
    >
      {upgradeToTrue && (
        <Box mt={2}>
          <TruePlatformWelcome
            onExploreClick={() => sendTrackingData(
              'subscription_page_all_action',
              accountId,
              undefined,
              {
                field: 'true_platform_welcome',
                object: 'explore_true_platform',
                action: 'click',
                plan_flow: 'true_bundle',
                ...trackingCookies,
              },
            )}
          />
        </Box>
      )}
      <SubscriptionV2Content trueSubscriptions={subscriptions} accountId={accountId} />
    </Page>
  );
};
